/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React from 'react';
import {v4} from 'uuid';
import {ENTER_CODE, ESCAPE_CODE, ESCAPE_IE_CODE} from "./constants";
import {Breakpoint} from "./interfaces";

export const generateUUID = (): string => {
    // Crypto is not supported by some browsers, so we use another method if crypto is not supported.
    if (window.crypto)
        // @ts-ignore
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: any) =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
    return v4();
};

export const isNotNull = (obj: any): boolean => {
    if (obj === undefined || obj === null)
        return false;
    return !(obj.hasOwnProperty('length') && obj.length === 0);
};

export const isNull = (obj: any): boolean => !isNotNull(obj);

export const hasHover = (element: HTMLElement | null): boolean => {
    if (!element) return false;
    if (isNotNull(element) && element.parentElement !== null) {
        return element.parentElement.querySelector(':hover') === element;
    }
    return false;
};

export const pxToRem = (pxValue: number) => {
    let fontSize: number = getFontSize();
    return pxValue / fontSize;
};

export const remToPx = (remValue: number) => {
    let htmlElement = document.getElementsByTagName('html')[0];
    let fontSize: number | string = window.getComputedStyle(htmlElement, null).getPropertyValue('font-size');
    fontSize = parseInt(fontSize.slice(0, fontSize.length - 2), 10);
    return remValue * fontSize;
};

const getFontSize = (): number => {
    let fontSize: number | string = getComputedProperty('font-size');
    fontSize = parseInt(fontSize.slice(0, fontSize.length - 2), 10);
    return fontSize;
};

export const getComputedProperty = (propertyName: string, element?: Element): string => {
    const htmlElement = element || document.getElementsByTagName('html')[0];
    return window.getComputedStyle(htmlElement, null).getPropertyValue(propertyName);
};

export const stringStartsWith = (string: string, value: string): boolean => {
    value = value.trim().toLowerCase();
    const valueLength = value.length;
    return string.toLowerCase().slice(0, valueLength) === value;
};

export const stringContains = (string: string, value: string): boolean =>
    string.toLowerCase().includes(value.toLowerCase());

export const voidFunction = () => null;

export const escapePressed = (evt: React.KeyboardEvent | KeyboardEvent) => {
    return ((evt.key && (evt.key === ESCAPE_IE_CODE || evt.key === ESCAPE_CODE))) || evt.keyCode === 27 || evt.which ===
        27;
};

export const enterPressed = (evt: React.KeyboardEvent | KeyboardEvent) => {
    return ((evt.key && evt.key === ENTER_CODE) || evt.keyCode === 13 || evt.which === 13);
};

let canvas: HTMLCanvasElement | null = null;
export const calculateTextWidth = (text: string, fontSize?: number, fontFamily?: string): number => {
    canvas = canvas || document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (fontSize === undefined) fontSize = getFontSize();
    if (fontFamily === undefined || !fontFamily.length) fontFamily = getComputedProperty('font-family');

    let width = 0;
    if (context) {
        context.font = `${fontSize}px ${fontFamily}`;
        width = context.measureText(text).width;
    }
    return Math.ceil(width);
};

export const pad = (num: string | number): string => ('0' + num).slice(-2);

export function padNum(num: string | number, maxLength: number = 2): string {
    return num.toString().padStart(maxLength, '0');
}

export const stopPropagation = (evt: Event | React.SyntheticEvent) => {
    evt.stopPropagation();
};

export const assert = (condition: boolean, errMsg?: string | null) => {
    const message = errMsg || 'Undocumented assertion error';
    if (!condition) {
        if (typeof Error !== 'undefined')
            throw new Error(message);
        else throw message;
    }
};